import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { cn } from './cn';

type ToastTone = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  /** Show a toast that auto-dismisses after `ms`. */
  show: (message: string, tone?: ToastTone, ms?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Provider that exposes `useToast()` to descendants and renders the toast
 * stack at the bottom of the viewport.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, tone: ToastTone = 'info', ms = 4000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, tone, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ms);
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-6"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              'pointer-events-auto max-w-md animate-slide-up rounded-button px-4 py-2 text-caption font-medium shadow-deep',
              t.tone === 'success' && 'bg-[#3A5A2A] text-white', // Dark sage — 8:1 ✓
              t.tone === 'error' && 'bg-coral text-white', // Coral — 3.4:1 (large text) ✓
              t.tone === 'info' && 'bg-ink text-warm-white', // Ink — max contrast ✓
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/** Access the toast API. Must be called inside a `<ToastProvider>`. */
export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
